import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  KeyRound, Plus, Copy, Check, Trash2, AlertTriangle,
  Loader2, ShieldCheck, ExternalLink, BookOpen,
} from "lucide-react";
import { toast } from "sonner";

/**
 * SCIM Provisioning Card
 * ──────────────────────
 * Renders below the federation source grid. Lets a Web Admin / BCM Manager:
 *   • See the per-org SCIM Base URL to give to their Entra admin
 *   • Mint a new bearer token (plaintext shown ONCE — copy then it's gone)
 *   • List all minted tokens (with previews like `scim_abc…wxyz`)
 *   • Revoke any token
 *
 * Intentionally minimal — no token edit, no expiry timer, no rotation
 * scheduler. SCIM tokens are long-lived by design; rotation is a customer
 * decision driven by Entra's own pipeline.
 */
export default function ScimProvisioningCard({ api, role }) {
  const allowed = role === "admin" || role === "bcm_manager";
  const [scimStatus, setScimStatus] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minting, setMinting] = useState(false);
  const [newName, setNewName] = useState("");
  const [mintedToken, setMintedToken] = useState(null); // {token, name, scim_base_url}
  const [copiedKey, setCopiedKey] = useState(null);
  const [revokingId, setRevokingId] = useState(null);

  const refresh = async () => {
    if (!allowed) { setLoading(false); return; }
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get("/scim-admin/status"),
        api.get("/scim-admin/tokens"),
      ]);
      setScimStatus(s.data);
      setTokens(t.data?.tokens || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load SCIM status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1800);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Could not copy — copy manually");
    }
  };

  const mint = async () => {
    const name = newName.trim() || `Token · ${new Date().toLocaleDateString()}`;
    setMinting(true);
    try {
      const res = await api.post("/scim-admin/tokens", { name });
      setMintedToken(res.data);
      setNewName("");
      await refresh();
      toast.success("SCIM token minted — copy it now, it can't be retrieved later");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mint token");
    } finally {
      setMinting(false);
    }
  };

  const revoke = async (id) => {
    setRevokingId(id);
    try {
      await api.post(`/scim-admin/tokens/${id}/revoke`);
      toast.success("Token revoked — Entra will start failing on its next sync.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    } finally {
      setRevokingId(null);
    }
  };

  if (!allowed) return null;

  return (
    <Card className="mb-6 border-emerald-200" data-testid="scim-provisioning-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <KeyRound className="w-5 h-5 text-emerald-700" />
              SCIM Provisioning <span className="text-xs font-normal text-slate-500 ml-1">(Entra · Okta · OneLogin)</span>
            </CardTitle>
            <p className="text-xs text-slate-600 mt-1 max-w-3xl">
              Issue your customer's identity provider a bearer token so they can <strong>push</strong> joiner / leaver / department
              changes into ResiliencyHUB in real-time. Use this when AD scheduled pulls aren't fast enough or when the customer
              wants Entra to be the system-of-record for user lifecycle.
            </p>
          </div>
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[0.7rem]">
            <ShieldCheck className="w-3 h-3 mr-1" /> SCIM 2.0 · RFC 7643/7644
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* SCIM Base URL — what the customer gives to their Entra admin */}
        {scimStatus && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="scim-base-url-row">
            <div className="text-[0.7rem] uppercase tracking-wider text-slate-500 font-bold mb-1">
              SCIM 2.0 Base URL <span className="text-slate-400 font-normal normal-case tracking-normal">— paste into Entra → Provisioning → Tenant URL</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="text-xs bg-white border border-slate-200 rounded px-2 py-1.5 flex-1 break-all min-w-0" data-testid="scim-base-url">
                {scimStatus.scim_base_url}
              </code>
              <Button
                size="sm" variant="outline"
                onClick={() => copy(scimStatus.scim_base_url, "url")}
                data-testid="scim-base-url-copy"
              >
                {copiedKey === "url" ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                Copy URL
              </Button>
            </div>
          </div>
        )}

        {/* Just-minted token banner — the ONLY moment the plaintext is visible */}
        {mintedToken && (
          <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 space-y-3" data-testid="scim-just-minted-banner">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-700" />
              <p className="font-bold text-sm text-amber-900">
                Copy this token now — ResiliencyHUB will never show it again
              </p>
            </div>
            <p className="text-xs text-amber-800">
              Give the SCIM Base URL above and the bearer token below to the customer's Entra admin via your secure channel
              (1Password share, PGP, Keeper). Entra pastes them into <em>Provisioning → Admin Credentials</em>.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="text-xs bg-white border border-amber-200 rounded px-2 py-1.5 flex-1 break-all min-w-0 font-mono" data-testid="scim-just-minted-token">
                {mintedToken.token}
              </code>
              <Button
                size="sm"
                onClick={() => copy(mintedToken.token, "token")}
                data-testid="scim-just-minted-token-copy"
                className="bg-amber-700 hover:bg-amber-800 text-white"
              >
                {copiedKey === "token" ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                Copy token
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={() => setMintedToken(null)}
                data-testid="scim-just-minted-dismiss"
              >
                I've copied it — dismiss
              </Button>
            </div>
          </div>
        )}

        {/* Mint new token */}
        <div className="rounded-lg border border-slate-200 p-3 bg-white">
          <div className="text-[0.7rem] uppercase tracking-wider text-slate-500 font-bold mb-2">
            Mint a new bearer token
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              type="text"
              placeholder="Token name (e.g. 'DivIHN Entra Production')"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="flex-1 min-w-[240px] text-sm"
              maxLength={120}
              disabled={minting}
              data-testid="scim-new-token-name"
            />
            <Button
              onClick={mint}
              disabled={minting || loading}
              data-testid="scim-mint-token-btn"
              className="bg-emerald-700 hover:bg-emerald-800"
            >
              {minting
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Minting…</>
                : <><Plus className="w-4 h-4 mr-2" /> Mint token</>}
            </Button>
          </div>
        </div>

        {/* Existing tokens */}
        <div className="rounded-lg border border-slate-200 bg-white">
          <div className="text-[0.7rem] uppercase tracking-wider text-slate-500 font-bold p-3 border-b border-slate-100">
            Active tokens · {tokens.filter((t) => !t.revoked_at).length} of {tokens.length}
          </div>
          {loading && (
            <div className="p-6 text-center text-slate-400 text-xs">
              <Loader2 className="w-4 h-4 mx-auto mb-2 animate-spin" /> Loading…
            </div>
          )}
          {!loading && tokens.length === 0 && (
            <div className="p-6 text-center text-slate-400 text-xs" data-testid="scim-no-tokens">
              No SCIM tokens yet. Mint one above to enable Entra push provisioning.
            </div>
          )}
          {!loading && tokens.length > 0 && (
            <ul className="divide-y divide-slate-100" data-testid="scim-token-list">
              {tokens.map((t) => (
                <li key={t.id} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`scim-token-${t.id}`}>
                  <KeyRound className={`w-4 h-4 flex-shrink-0 ${t.revoked_at ? "text-slate-300" : "text-emerald-600"}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${t.revoked_at ? "text-slate-400 line-through" : "text-slate-800"}`}>
                      {t.name}
                    </p>
                    <p className="text-[0.7rem] text-slate-500 truncate font-mono">
                      {t.token_preview} · created {new Date(t.created_at).toLocaleDateString()}
                      {t.last_used_at && <> · last used {new Date(t.last_used_at).toLocaleDateString()}</>}
                      {t.revoked_at && <span className="text-rose-500"> · revoked {new Date(t.revoked_at).toLocaleDateString()}</span>}
                    </p>
                  </div>
                  {!t.revoked_at && (
                    <Button
                      size="sm" variant="outline"
                      onClick={() => revoke(t.id)}
                      disabled={revokingId === t.id}
                      data-testid={`scim-token-revoke-${t.id}`}
                      className="text-rose-700 hover:text-rose-800 hover:bg-rose-50 border-rose-200"
                    >
                      {revokingId === t.id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <><Trash2 className="w-3.5 h-3.5 mr-1" /> Revoke</>}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <a
          href="/Federation_Activation_Runbook.html#troubleshoot"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-indigo-700 hover:text-indigo-900 font-semibold"
          data-testid="scim-runbook-link"
        >
          <BookOpen className="w-3.5 h-3.5" />
          How to wire Entra to push to this URL · Runbook §10
          <ExternalLink className="w-3 h-3" />
        </a>
      </CardContent>
    </Card>
  );
}
